import React, { FormEvent } from 'react';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import api from '../../../components/api';

function useProducts() {
  // Switch to show all product or only wholesale
  const [step, setStep] = React.useState<number>(0);

  // Product Category List and Handler
  const [currentCategory, setCurrentCategory] = React.useState<{value: number, label: string} | null>(null);
  const { data: categoryRes, isLoading: categoryLoad } = useQuery(
    ['getAllCategories', 'product'],
    () => api.product.getAllCategories(),
  );
  const categoryList = React.useMemo(() => {
    const resData = categoryRes?.data?.data;
    if (resData) {
      return resData.map((category) => ({
        value: category.id,
        label: category.category,
      }));
    }
    return [];
  }, [categoryRes]);

  // Search Handler
  const [searchValue, setSearchValue] = React.useState<string>('');
  // Current Page Handler
  const [currentPage, setCurrentPage] = React.useState<number>(1);
  // Product List Handler
  const { data: productRes, isLoading: productLoad, refetch: refetchProduct } = useQuery(
    ['findAll', 'product', step !== 0, currentPage - 1, 20],
    () => api.product.findAll(searchValue, currentPage - 1, 20, step !== 0),
  );
  // Product with Category
  const { data: prodByCategoryRes, isLoading: prodByCategoryLoad } = useQuery(
    ['getAllByCategoryId', 'product', currentCategory?.value, currentPage - 1, 20, step !== 0],
    () => api.product.getAllByCategoryId(currentCategory!.value, currentPage - 1, 20, step !== 0),
    {
      enabled: !!currentCategory,
    },
  );

  const productList = React.useMemo(() => {
    const resProduct = productRes?.data;
    const resByCategory = prodByCategoryRes?.data;
    if (resByCategory && currentCategory && resByCategory?.success) {
      return {
        totalPage: resByCategory.totalPages,
        data: resByCategory.data,
      };
    }
    if (resProduct && resProduct?.success) {
      return {
        totalPage: resProduct.totalPages,
        data: resProduct.data,
      };
    }
    return undefined;
  }, [productRes, currentCategory, prodByCategoryRes]);

  // this needed for after delete product
  const handleRefetch = () => {
    refetchProduct().catch((e) => console.log(e));
  };

  const onSearch = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setCurrentCategory(null);
    setCurrentPage(1);
    handleRefetch();
  };

  // Navigation Handler
  const navigation = useNavigate();
  const navigateToAdd = () => navigation('/products/add');

  React.useEffect(() => {
    handleRefetch();
  }, [step]);

  return {
    navigation: {
      add: navigateToAdd,
    },
    step: {
      current: step,
      setCurrent: setStep,
    },
    search: {
      current: searchValue,
      setCurrent: setSearchValue,
    },
    product: {
      list: productList,
      load: productLoad || prodByCategoryLoad,
      refetch: handleRefetch,
    },
    category: {
      list: categoryList,
      load: categoryLoad,
      current: currentCategory,
      setCurrent: setCurrentCategory,
    },
    page: {
      current: currentPage,
      setCurrent: setCurrentPage,
    },
    onSearch,
  };
}

export default useProducts;
